import type { CaseCallAction, CaseRequestQueue, CaseRequestStatus } from '../../lib/api/requests';

/**
 * Every `t()` key this module (and `CaseRequests.tsx`) needs for the four
 * closed vocabularies `requests.ts` carries — `queue`, `status`,
 * `next_action`'s `action` vocabulary, and the empty-state copy per queue.
 *
 * `as const satisfies Record<...>` — the SAME shape `caseStatusLabel.ts`
 * documents at length: a template-literal key (`` t(`requests.status.${x}`)
 * ``) compiles but fails `npm run typecheck` against the typed `t()`
 * overloads `i18next.d.ts` installs (task 19), because TypeScript widens
 * the template to `string`, which is not a key `en.json`'s shape admits.
 * These objects are the one place each vocabulary is mapped to a literal
 * key, so every call site reads a literal rather than building one.
 */

export const QUEUE_LABEL_KEY = {
  open: 'requests.queue.open',
  awaiting: 'requests.queue.awaiting',
  complete: 'requests.queue.complete',
} as const satisfies Record<CaseRequestQueue, string>;

export const QUEUE_EMPTY_KEY = {
  open: 'requests.empty.open',
  awaiting: 'requests.empty.awaiting',
  complete: 'requests.empty.complete',
} as const satisfies Record<CaseRequestQueue, string>;

export const REQUEST_STATUS_LABEL_KEY = {
  open: 'requests.status.open',
  in_progress: 'requests.status.inProgress',
  complete: 'requests.status.complete',
  cancelled: 'requests.status.cancelled',
} as const satisfies Record<CaseRequestStatus, string>;

export const CALL_ACTION_LABEL_KEY = {
  reached: 'requests.callAction.reached',
  no_reply: 'requests.callAction.no_reply',
  unreachable: 'requests.callAction.unreachable',
} as const satisfies Record<CaseCallAction, string>;

/**
 * The nine `request.*` template codes, as something a person reads.
 *
 * NOT the template's own `name`, which the API could equally have published:
 * that column holds one English string per template, and this application has
 * an Arabic half. A code is a stable identifier; its label belongs in the
 * bundles with every other piece of copy.
 *
 * DELIBERATELY NOT EXHAUSTIVE over anything. `kind` is derived from whatever
 * template the first message used (spec D-3), and migrated rows carry legacy
 * types that were never seeded — so callers fall back to the raw code rather
 * than rendering an empty cell. A `satisfies Record<...>` here would be a
 * promise this map cannot keep.
 */
export const REQUEST_KIND_LABEL_KEY = {
  'request.sensor_number': 'requests.kindLabel.sensor_number',
  'request.serial_number': 'requests.kindLabel.serial_number',
  'request.receipt': 'requests.kindLabel.receipt',
  'request.wasfati': 'requests.kindLabel.wasfati',
  'request.insurance_card': 'requests.kindLabel.insurance_card',
  'request.error_photo': 'requests.kindLabel.error_photo',
  'request.diabetes_readings': 'requests.kindLabel.diabetes_readings',
  'request.event_log': 'requests.kindLabel.event_log',
  'request.wrong_sms': 'requests.kindLabel.wrong_sms',
} as const;
